import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import EmailField from '../_molecules/EmailField/EmailField';
import PasswordField from '../_molecules/PasswordField/PasswordField';
import Button from '../_atomic/Button/Button';
import CustomRRLink from '../_atomic/CustomRRLink/CustomRRLink';
import { TEXT_FIELD_STATUS, ERROR, publicHash, CONTEXT_PROVIDERS } from '../../utils/constants';
import AuthFormContext from './AuthFormContext';
import SocialLoginWrapper from '../../containers/SocialLoginWrapper';
import FacebookLoginWrapper from '../../containers/SocialLoginWrapper/FacebookIndex';
import ErrorModal from '../_molecules/ErrorModal/ErrorModal';
import { getFieldInitialState } from '../../utils/helpers';
import './AuthForm.scss';

const emailFieldName = 'user-email';
const passwordFieldName = 'user-password';
// options for use at Login and Sign up form with slightly different content {'
// '} help to save spaces, when the editor format code
const options = {
  afterFieldsContent: {
    login: <CustomRRLink to={`${publicHash}/auth/restore-password`} text="Forgot password" />,
    registration: (
      /* eslint-disable */
      <p className="plain-text">
        By creating an account you agree with the{' '}
        <a href="https://advaice.com/docs/terms-of-service.pdf" target="_blank"> Terms of use</a>
      </p>
      /* eslint-enable */
    ),
  },
  bottomContent: {
    login: (
      <p className="plain-text">
        Don’t have an account?{' '}
        <CustomRRLink to={`${publicHash}/auth/registration`} text="Sign up" />
      </p>
    ),
    registration: (
      <p className="plain-text">
        Have an account?{' '}
        <CustomRRLink to={`${publicHash}/auth/login`} text="Log in" />
      </p>
    ),
  },
};

class AuthForm extends Component {
  static propTypes = {
    label: PropTypes.string,
    buttonLabel: PropTypes.string.isRequired,
    className: PropTypes.string,
    type: PropTypes.string.isRequired,
    submitHandler: PropTypes.func.isRequired,
    status: PropTypes.string.isRequired,
    error: PropTypes.string.isRequired,
    specifiedValidator: PropTypes.shape({ email: PropTypes.bool, password: PropTypes.bool }),
    annulError: PropTypes.func.isRequired,
  };

  static defaultProps = {
    label: '',
    className: '',
    specifiedValidator: {
      email: false,
      password: false,
    },
  };
  constructor() {
    super();
    this.fields = {};
  }

  state = {
    formStatus: TEXT_FIELD_STATUS.INITIAL,
    fields: {
      [emailFieldName]: getFieldInitialState(),
      [passwordFieldName]: getFieldInitialState(),
    },
  };
  getFormStatus = (fields) => {
    let status = TEXT_FIELD_STATUS.INITIAL;
    let validFields = 0;
    let invalidFields = 0;
    const fieldsNames = Object.keys(fields);
    /* calc invalid fields coz form can have one VALID and one INITIAL field
       then form isn't valid or invalid it is initial */
    fieldsNames.forEach((name) => {
      if (fields[name].status === TEXT_FIELD_STATUS.VALID) { validFields += 1; }
      if (fields[name].status === TEXT_FIELD_STATUS.INVALID) { invalidFields += 1; }
    });
    if (validFields === fieldsNames.length) {
      status = TEXT_FIELD_STATUS.VALID;
    }

    if (invalidFields > 0) {
      status = TEXT_FIELD_STATUS.INVALID;
    }
    return status;
  };
  registerField = ({ name, validator }) => {
    const { type } = this.props;
    this.fields[name] = {
      validator,
    };
    if (type === 'registration') {
      const fields = { ...this.state.fields };
      fields[passwordFieldName].hint = 'Should be at least 6 symbols, contain uppercase, lowercase and one number';
      this.setState(() => ({ fields }));
    }
  };

  fieldChangeHandler = ({ name, value }) => {
    const fields = { ...this.state.fields };
    let formStatus = { ...this.state.formStatus };
    fields[name].value = value;

    if (fields[name].status !== TEXT_FIELD_STATUS.INITIAL) {
      const { validator } = this.fields[name];
      const validate = validator(fields[name].value);
      fields[name].status = validate.status;
      fields[name].errorText = validate.errorText;
      formStatus = this.getFormStatus(fields);
    }

    this.setState(() => ({ fields, formStatus }));
  };

  fieldFocusHandler = ({ name, isFocused }) => {
    const fields = { ...this.state.fields };
    let { formStatus } = this.state;
    fields[name].isFocused = isFocused;
    if (!isFocused) {
      const { validator } = this.fields[name];
      const validate = validator(fields[name].value);
      fields[name].status = validate.status;
      fields[name].errorText = validate.errorText;
      formStatus = this.getFormStatus(fields);
    }
    this.setState(() => ({ fields, formStatus }));
  };

  submitHandler = (e) => {
    e.preventDefault();
    const fields = { ...this.state.fields };
    Object
      .keys(this.fields)
      .forEach((name) => {
        const { validator } = this.fields[name];
        const validate = validator(fields[name].value);
        fields[name].status = validate.status;
        fields[name].errorText = validate.errorText;
      });
    const formStatus = this.getFormStatus(fields);
    this.setState(() => ({ fields, formStatus }), () => {
      if (formStatus === TEXT_FIELD_STATUS.VALID) {
        const data = {
          username: fields[emailFieldName].value,
          password: fields[passwordFieldName].value,
        };
        this
          .props
          .submitHandler(data);
      }
    });
  };

  render() {
    const {
      label,
      buttonLabel,
      className,
      type,
      status,
      error,
      annulError,
      specifiedValidator,
    } = this.props;

    const { formStatus, fields } = this.state;
    const authFormClasses = classNames({
      'auth-form': true,
      [`${className}`]: true,
    });

    const emailField = fields[emailFieldName];
    const passwordField = fields[passwordFieldName];
    const isError = status === ERROR;
    const disabledButton = formStatus === TEXT_FIELD_STATUS.INVALID;
    return (
      <div>
        <form onSubmit={this.submitHandler} className={authFormClasses}>
          <ErrorModal requestClose={annulError} visible={isError} error={error} />

          <div className="main-label">
            <span>{label}</span>
          </div>
          <AuthFormContext.Provider
            value={{
            register: this.registerField,
            changeHandler: this.fieldChangeHandler,
            focusHandler: this.fieldFocusHandler,
          }}
          >
            <EmailField
              name={emailFieldName}
              value={emailField.value}
              status={emailField.status}
              isFocused={emailField.isFocused}
              errorText={emailField.errorText}
              hint={emailField.hint}
              isRequired
              needSpecifiedValidator={specifiedValidator.email}
              contextProvider={CONTEXT_PROVIDERS.AuthFormContext}
            />
            <PasswordField
              name={passwordFieldName}
              value={passwordField.value}
              status={passwordField.status}
              isFocused={passwordField.isFocused}
              errorText={passwordField.errorText}
              hint={passwordField.hint}
              contextProvider={CONTEXT_PROVIDERS.AuthFormContext}
              needSpecifiedValidator={specifiedValidator.password}
              isRequired
            />
            <div className="after-fields-content-wrapper">{options.afterFieldsContent[type]}</div>
            <div className="form-buttons-wrapper">
              <Button label={buttonLabel} disabled={disabledButton} type="submit" />
              <div className="social-buttons-wrapper" >
                <SocialLoginWrapper />
              </div>
            </div>
            <div className="bottom-content-wrapper">{options.bottomContent[type]}</div>
          </AuthFormContext.Provider>
        </form>
        <div className="form-facebook-wrapper">
          <FacebookLoginWrapper />
        </div>
      </div>
    );
  }
}

export default AuthForm;



// WEBPACK FOOTER //
// ./src/components/AuthForm/AuthForm.js